import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import CreatableSelect from 'react-select/creatable'
import { Clock3, Share2 } from 'lucide-react'
import api from 'api/api'
import Tray from 'components/shared/Tray'
import { PurpleButton } from 'components/shared/buttons/Button'
import { themedUserInviteSelectStyles } from 'components/shared/reactSelectStyles'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import { buildStreamInviteUrl } from 'components/teams/streamInviteUrl'
import {
    inviteEmailRegex,
    resolveSentInvites,
    splitInviteSelections,
} from 'components/teams/inviteRecipients'
import { insertToClipboard } from 'utils'
import { isMobile } from 'lib/device'
import { hasNativeCapability, shareTextOrUrlNative } from 'utils/nativeShare'
import styles from 'components/teams/stream-invite-tray.module.scss'

// The tray overlay sits at z-index 9999, so the select's portaled menu has to
// clear it (the settings modal's 1701 would render underneath).
const inviteTraySelectStyles = {
    ...themedUserInviteSelectStyles,
    menuPortal: base => ({
        ...base,
        zIndex: 10001,
    }),
}

const filterOption = (option, inputValue) =>
    option.value.toLowerCase().startsWith(inputValue.toLowerCase())

// Owner-facing bottom sheet dedicated to sending stream invites: the standing
// invite link up top, direct username/email invites below. Management (pending
// list, member removal) stays in Stream settings.
export default function StreamInviteTray({ isOpen, team, onDismiss, onManageStream }) {
    const teamId = team?.id
    const [fullTeam, setFullTeam] = useState(null)
    const [users, setUsers] = useState(null)
    const [selectedOptions, setSelectedOptions] = useState([])
    const [sending, setSending] = useState(false)
    const [note, setNote] = useState(null)
    // Who this sitting at the tray has invited, so the sends stay on screen
    // instead of collapsing into a bare count.
    const [sentInvites, setSentInvites] = useState([])
    const [linkCopied, setLinkCopied] = useState(false)
    const [savingLink, setSavingLink] = useState(false)
    const [linkNote, setLinkNote] = useState(null)

    // The header's team lookup can hand us a lean row without members, pending
    // invites, or the standing-invite flag, so fetch the full payload on open.
    useEffect(() => {
        if (!isOpen || !teamId) return
        let cancelled = false
        setFullTeam(null)
        setUsers(null)
        setSelectedOptions([])
        setNote(null)
        setLinkCopied(false)
        setLinkNote(null)
        setSentInvites([])
        api.getTeam(teamId)
            .then(apiTeam => {
                if (!cancelled && apiTeam?.id) setFullTeam(apiTeam)
            })
            .catch(error => console.error('Error fetching team for invite tray:', error))
        return () => {
            cancelled = true
        }
    }, [isOpen, teamId])

    useEffect(() => {
        if (!isOpen || !fullTeam?.id) return
        let cancelled = false
        api.getUsers({ space_id: fullTeam.space_id })
            .then(apiUsers => {
                if (!cancelled) setUsers(apiUsers)
            })
            .catch(error => console.error('Error fetching users for invite tray:', error))
        return () => {
            cancelled = true
        }
    }, [isOpen, fullTeam?.id])

    const displayTeam = fullTeam?.id === teamId ? fullTeam : team
    // Until the full team lands, the flag can only come from the header's lean
    // copy — which may be stale. Writing from that would flip the link the
    // wrong way, so the switch stays inert until we have the server's answer.
    const teamLoaded = fullTeam?.id === teamId
    const linkEnabled = teamLoaded && !!fullTeam?.standing_invitations_allowed
    const inviteUrl = teamId ? buildStreamInviteUrl(teamId, window.location.origin) : ''

    const invitedUserIds = useMemo(
        () =>
            new Set(
                [...(displayTeam?.users || []), ...(displayTeam?.pending_users || [])].map(
                    user => user?.id,
                ),
            ),
        [displayTeam],
    )
    const userOptions = useMemo(
        () =>
            (users || [])
                .filter(user => !invitedUserIds.has(user?.id))
                .filter(user => user?.id != gon?.currentUser?.id)
                .map(user => ({ value: user?.name, label: user?.name })),
        [users, invitedUserIds],
    )
    const recipients = useMemo(
        () => splitInviteSelections(selectedOptions, users),
        [selectedOptions, users],
    )
    const recipientCount = recipients.emails.length + recipients.ids.length
    const pendingInviteCount =
        (displayTeam?.invitations?.length || 0) + (displayTeam?.pending_users?.length || 0)

    const canUseNativeShare =
        hasNativeCapability('nativeShare') ||
        (isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function')

    const copyInviteLink = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        insertToClipboard(inviteUrl)
        setLinkCopied(true)
        window.setTimeout(() => setLinkCopied(false), 1500)
    }

    const shareInviteLink = async ev => {
        ev.preventDefault()
        ev.stopPropagation()
        const payload = {
            title: `Join ${displayTeam?.name || 'this channel'} on Treechat`,
            url: inviteUrl,
        }
        try {
            if (hasNativeCapability('nativeShare')) {
                await shareTextOrUrlNative(payload)
            } else {
                await navigator.share(payload)
            }
        } catch (error) {
            if (error?.name !== 'AbortError') {
                console.error('Failed to share invite link', error)
            }
        }
    }

    // standing_invitations_allowed is one flag for the whole stream, and the
    // server checks it on both the landing page and the join, so switching it
    // off also retires links already in circulation.
    const setInviteLinkAllowed = async allowed => {
        if (savingLink || !teamLoaded) return
        setSavingLink(true)
        setLinkCopied(false)
        setLinkNote(null)
        try {
            const updated = await api.updateTeam(teamId, {
                standing_invitations_allowed: allowed,
            })
            if (updated?.id) {
                setFullTeam(updated)
                setLinkNote({
                    kind: 'success',
                    text: allowed ? 'Invite link enabled.' : 'Invite link disabled.',
                })
            } else {
                setLinkNote({ kind: 'error', text: 'Could not update the invite link.' })
            }
        } catch (error) {
            console.error('Error updating invite link setting:', error)
            setLinkNote({ kind: 'error', text: 'Could not update the invite link.' })
        } finally {
            setSavingLink(false)
        }
    }

    const sendInvites = async ev => {
        ev?.preventDefault?.()
        if (sending || !recipientCount) return
        setSending(true)
        setNote(null)
        try {
            const data = await api.addToTeam(
                { emails: recipients.emails, ids: recipients.ids },
                teamId,
            )
            if (data?.team?.id) setFullTeam(data.team)
            if (data?.team?.id && !data?.failed_emails?.length) {
                setSelectedOptions([])
                const justSent = resolveSentInvites(recipients, data.team, users)
                setSentInvites(previous => {
                    const seen = new Set(previous.map(invite => invite.key))
                    return [...previous, ...justSent.filter(invite => !seen.has(invite.key))]
                })
                setNote(null)
            } else {
                setNote({
                    kind: 'error',
                    text: data?.failed_emails?.length
                        ? 'Some invites could not be sent. Please try again.'
                        : 'Could not send invites. Please try again.',
                })
            }
        } catch (error) {
            console.error('Error sending stream invites:', error)
            setNote({ kind: 'error', text: 'Could not send invites. Please try again.' })
        } finally {
            setSending(false)
        }
    }

    if (!isOpen || !teamId) return null
    if (typeof document === 'undefined') return null

    return createPortal(
        <Tray
            title={`Invite to ${displayTeam?.name || 'channel'}`}
            onDismiss={onDismiss}
            trayClassName={styles.inviteTray}
            bodyClassName={styles.inviteTrayBody}
            closeLabel="Close invite options"
            dialogTestId="stream-invite-tray"
            closeButtonTestId="stream-invite-tray-close"
        >
            <div className={styles.section}>
                <div className={styles.sectionHeaderRow}>
                    <div className={styles.sectionLabel}>Invite link</div>
                    <label
                        className={cn(
                            styles.toggleSwitch,
                            (savingLink || !teamLoaded) && styles.toggleSaving,
                        )}
                        data-testid="stream-invite-link-toggle-control"
                    >
                        <input
                            type="checkbox"
                            checked={linkEnabled}
                            disabled={savingLink || !teamLoaded}
                            onChange={ev => setInviteLinkAllowed(ev.target.checked)}
                            aria-label="Allow invite link"
                            data-testid="stream-invite-link-toggle"
                        />
                        <span className={styles.toggleSlider} />
                    </label>
                </div>
                {!teamLoaded ? (
                    <div className={styles.helperText}>Loading invite link…</div>
                ) : linkEnabled ? (
                    <>
                        <div className={styles.linkRow}>
                            <div
                                className={styles.linkBox}
                                title={inviteUrl}
                                data-testid="stream-invite-link-url"
                            >
                                {inviteUrl}
                            </div>
                            <button
                                type="button"
                                onClick={copyInviteLink}
                                className={styles.submitReset}
                                data-testid="stream-invite-link-copy"
                            >
                                <PurpleButton
                                    text={linkCopied ? 'Copied!' : 'Copy'}
                                    contextStyles={styles.linkActionButton}
                                />
                            </button>
                        </div>
                        {canUseNativeShare && (
                            <button
                                type="button"
                                className={styles.nativeShareButton}
                                onClick={shareInviteLink}
                                data-testid="stream-invite-native-share"
                            >
                                <Share2 size={16} />
                                Share link…
                            </button>
                        )}
                        <div className={styles.helperText}>
                            Anyone with this link can join this channel.
                        </div>
                    </>
                ) : (
                    <div className={styles.helperText}>
                        Turn this on to share a link anyone can use to join. While it is off, links
                        you have already shared stop working.
                    </div>
                )}
                {linkNote && (
                    <div
                        className={cn(styles.note, linkNote.kind === 'error' && styles.noteError)}
                        role="status"
                        data-testid="stream-invite-link-status"
                    >
                        {linkNote.text}
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <label className={styles.sectionLabel} htmlFor="stream-invite-recipient-input">
                    Invite people directly
                </label>
                <form onSubmit={sendInvites}>
                    <div className={styles.inviteSelect} {...panelSwipeExclusionProps}>
                        <CreatableSelect
                            inputId="stream-invite-recipient-input"
                            instanceId="stream-invite-recipient"
                            isMulti
                            value={selectedOptions}
                            onChange={options => setSelectedOptions(options || [])}
                            styles={inviteTraySelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder={
                                users ? 'Type a username or email address…' : 'Loading people…'
                            }
                            filterOption={filterOption}
                            isValidNewOption={value => inviteEmailRegex.test(value)}
                            formatCreateLabel={value => `Invite "${value}" by email`}
                            noOptionsMessage={() =>
                                'No matching users — type a full email address to invite by email'
                            }
                            options={userOptions}
                        />
                    </div>
                    <div className={styles.sendRow}>
                        <button
                            type="submit"
                            className={styles.submitReset}
                            disabled={!recipientCount || sending}
                            data-testid="stream-invite-send"
                        >
                            <PurpleButton
                                text={sending ? 'Sending…' : 'Send invites'}
                                disabled={!recipientCount || sending}
                            />
                        </button>
                    </div>
                </form>
                {note && (
                    <div
                        className={cn(styles.note, note.kind === 'error' && styles.noteError)}
                        role="status"
                    >
                        {note.text}
                    </div>
                )}

                {sentInvites.length > 0 && (
                    <div
                        className={styles.sentBlock}
                        role="status"
                        data-testid="stream-invite-sent"
                    >
                        <div className={styles.sentHeading}>Pending</div>
                        <div className={styles.sentList}>
                            {sentInvites.map(invite => (
                                <span
                                    key={invite.key}
                                    className={styles.sentChip}
                                    data-testid="stream-invite-sent-chip"
                                >
                                    <Clock3 size={13} aria-hidden="true" />
                                    {invite.label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.footerRow}>
                <span className={styles.pendingCount}>
                    {pendingInviteCount > 0 &&
                        `${pendingInviteCount} pending ${
                            pendingInviteCount === 1 ? 'invite' : 'invites'
                        }`}
                </span>
                {onManageStream && (
                    <button type="button" className={styles.manageLink} onClick={onManageStream}>
                        Manage members in Channel settings
                    </button>
                )}
            </div>
        </Tray>,
        document.body,
    )
}
